import { IMatrix4, IVector3, LineType, rotate } from '@inter-ikea-betrakta/viewer';
import { ClickEventComponent, Vector, ComponentIds } from '@inter-ikea-core/core';
import { rgb, toIVector3, toMeters, toVector } from '../util';
import { IPEXViewerObject } from './IPEXViewerObject';
import earcut from 'earcut';
import { ProxyLoader } from '@inter-ikea-betrakta/viewer/dist/modules/ProxyLoader';
import { AdapterUtils } from './AdapterUtils';
export class IPEXViewerCoreAdapter {
    ipexViewer;
    entityManager;
    sceneManager;
    advancedViewer;
    constructor(ipexViewer, entityManager, sceneManager) {
        this.ipexViewer = ipexViewer;
        this.entityManager = entityManager;
        this.sceneManager = sceneManager;
        this.advancedViewer = ipexViewer;
        ipexViewer.get_click_void_observable().add((sender, event) => {
            const coords = new Vector(event.canvas_x, event.canvas_y, 0);
            const clickComponent = new ClickEventComponent(coords);
            const scene = sceneManager.scene;
            entityManager.addComponent(scene, clickComponent);
            entityManager.removeComponent(scene, clickComponent.typeId);
        });
    }
    uiLineAlwaysVisible(_isAlwaysVisible) {
        throw new Error('Method not implemented.');
    }
    resize() {
        this.ipexViewer.resize();
    }
    loadModel(entity, modelUrl, textureFile, modelLoaded, modelLoadFailed, loadingBoxSize, loadingBoxCenter) {
        const modelTransform = entity?.getComponent(ComponentIds.MODEL_TRANSFORM_COMPONENT_ID);
        const modelMatrix = modelTransform != null ? AdapterUtils.modelTransformToMatrix(modelTransform) : IMatrix4.Identity();
        let objectId = -1;
        if (loadingBoxSize && loadingBoxCenter) {
            const proxyLoader = new ProxyLoader(this.ipexViewer, modelUrl, toIVector3(loadingBoxSize), toIVector3(loadingBoxCenter), modelMatrix);
            objectId = proxyLoader.load_model_with_box_proxy();
        }
        else {
            objectId = this.ipexViewer.load_model(modelUrl, undefined, modelMatrix);
        }
        const viewerObject = new IPEXViewerObject(objectId, this.ipexViewer, this.entityManager, entity);
        viewerObject.setReceiveShadows(true);
        viewerObject.registerInputEvents();
        this.ipexViewer
            .object_ready(objectId)
            .then(() => {
            if (modelLoaded)
                modelLoaded(viewerObject);
        })
            .catch(() => {
            if (modelLoadFailed)
                modelLoadFailed(viewerObject);
        });
        return viewerObject;
    }
    existsModel(objId) {
        return this.ipexViewer.object_exists(objId);
    }
    worldCoordToCanvas(x, y, z) {
        const result = this.ipexViewer.project_world_position_to_canvas(toMeters(x), toMeters(y), toMeters(z));
        if (result) {
            return new Vector(result.x, result.y, 0);
        }
        return null;
    }
    raycast(x, y) {
        const visibleObjects = this.ipexViewer.get_all_objects().filter(id => this.ipexViewer.get_visible(id));
        const result = this.ipexViewer.raycast_object_using_canvas_coordinate(x, y, visibleObjects);
        if (result) {
            return toVector(result.world_position);
        }
        return null;
    }
    groupMeshes(meshes, entity) {
        const parent = this.ipexViewer.create_empty_object();
        const meanPos = meshes
            .map(m => m.getPosition())
            .reduce((prev, curr) => prev.plus(curr), new Vector(0, 0, 0))
            .times(1.0 / meshes.length);
        const viewerPos = toIVector3(meanPos);
        this.ipexViewer.set_position(parent, viewerPos.x, viewerPos.y, viewerPos.z);
        meshes.forEach(mesh => {
            this.ipexViewer.set_parent(mesh.getViewerObjectId(), parent);
            const currentPos = mesh.getPosition();
            const newPos = currentPos.minus(meanPos);
            const newViewerPos = toIVector3(newPos);
            this.ipexViewer.set_position(mesh.getViewerObjectId(), newViewerPos.x, newViewerPos.y, newViewerPos.z);
        });
        return new IPEXViewerObject(parent, this.ipexViewer, this.entityManager, entity);
    }
    debug(_enable) {
        console.warn('If you want debug support for the ipex viewer, you need to talk directly to the viewer apis');
    }
    createSphere(radius, entity) {
        const viewerRadius = toMeters(radius);
        // segments is a value of sphere "roundness", higher value means a smoother sphere, but probably
        // lower performance.
        const segments = 10;
        const material = this.ipexViewer.create_material();
        this.ipexViewer.set_roughness(material, 1);
        this.ipexViewer.set_metallic(material, 0);
        this.ipexViewer.set_base_color(material, rgb(1, 0, 0));
        const sphereId = this.ipexViewer.create_sphere(segments, 2 * viewerRadius, material);
        this.ipexViewer.set_material(sphereId, material);
        return new IPEXViewerObject(sphereId, this.ipexViewer, this.entityManager, entity);
    }
    createBox(width, height, depth, entity) {
        const material = this.ipexViewer.create_material();
        this.ipexViewer.set_roughness(material, 1);
        this.ipexViewer.set_metallic(material, 0);
        this.ipexViewer.set_base_color(material, rgb(0.955, 0.637, 0.538));
        const boxId = this.ipexViewer.create_box(toMeters(width), toMeters(height), toMeters(depth), material);
        this.ipexViewer.set_material(boxId, material);
        const vo = new IPEXViewerObject(boxId, this.ipexViewer, this.entityManager, entity);
        return vo;
    }
    cloneObject(vo, entity) {
        const objId = this.ipexViewer.clone_object(vo.getViewerObjectId());
        return new IPEXViewerObject(objId, this.ipexViewer, this.entityManager, entity);
    }
    createEmpty(entity) {
        const nodeId = this.ipexViewer.create_empty_object();
        return new IPEXViewerObject(nodeId, this.ipexViewer, this.entityManager, entity);
    }
    createPlane(width, depth, entity) {
        const planeId = this.ipexViewer.create_plane(toMeters(width), toMeters(depth));
        const planeMaterial = this.ipexViewer.create_material();
        this.ipexViewer.set_material(planeId, planeMaterial);
        this.ipexViewer.set_roughness(planeMaterial, 1);
        this.ipexViewer.set_metallic(planeMaterial, 0);
        this.ipexViewer.set_base_color(planeMaterial, rgb(0.66, 0.58, 0.49));
        return new IPEXViewerObject(planeId, this.ipexViewer, this.entityManager, entity || undefined);
    }
    //\left(u_2v_3-u_3v_2,\:u_3v_1-u_1v_3,\:u_1v_2-u_2v_1\right)
    vectorCross(u, v) {
        return toIVector3(new Vector(u.y * v.z - u.z * v.y, u.z * v.x - u.x * v.z, u.x * v.y - u.y * v.x));
    }
    createSurface(vertices, normal, entity) {
        // To use earcut the points must be on the aligned on the same z depth.
        // Because of this I need to rotate all the points so they align to (0,0,1)
        // I use the zNormal and do a cross to rotate all the points so they are on the same z
        const zNormal = IVector3.normalize(toIVector3(new Vector(0, 0, 1)));
        const iNormal = IVector3.normalize(toIVector3(normal));
        // Had to create my own cross function since the viewer cross seems to calcuate wrong in some cases.
        const rotateAroundVector = this.vectorCross(zNormal, iNormal);
        const dotProd = IVector3.dot(zNormal, iNormal);
        // No need to divide by vector length since theye ar normilized
        const degreeToRotate = Math.acos(dotProd);
        const vertices_aligned_to_z = vertices
            .map((v) => {
            const rotated = rotate(toIVector3(v), -degreeToRotate, rotateAroundVector);
            return [rotated.x, rotated.y, rotated.z];
        })
            .flat();
        // Earcutting the polygon
        // returns a list of the vertices for each triangle.
        const trianglesVertices = earcut(vertices_aligned_to_z, undefined, 3);
        // Adding some offset so the surface is not at the exact same location as the object plane.
        // This leads to flicker (offsetting by 5 mm)
        const offset = 0.005;
        const triangles = vertices
            .map((v) => {
            return [
                toMeters(v.X) + iNormal.x * offset,
                toMeters(v.Y) + iNormal.y * offset,
                toMeters(v.Z) + iNormal.z * offset
            ];
        })
            .flat();
        // Special case when normal of wall is aligned with zNormal.
        // In this case the drawing of the triangles need to be reversed (clockwise)
        if (normal.X === 0 && normal.Y === 0 && normal.Z === -1) {
            for (let k = 0; k < trianglesVertices.length; k += 3) {
                const swapped = trianglesVertices[k];
                trianglesVertices[k] = trianglesVertices[k + 2];
                trianglesVertices[k + 2] = swapped;
            }
        }
        const planeMaterial = this.ipexViewer.create_material();
        this.ipexViewer.set_metallic(planeMaterial, 0);
        this.ipexViewer.set_opacity(planeMaterial, 0.25);
        this.ipexViewer.set_base_color(planeMaterial, rgb(0, 0, 0.6));
        // Creates a custom mesh
        const planeId = this.advancedViewer.create_custom_mesh({
            positions: new Float32Array(triangles),
            indices: new Uint32Array(trianglesVertices)
        }, planeMaterial);
        return new IPEXViewerObject(planeId, this.ipexViewer, this.entityManager, entity || undefined);
    }
    createUiLine(start, end, color, thickness) {
        const lineId = this.ipexViewer.create_ui_line(LineType.SOLID, toIVector3(start), toIVector3(end), rgb(color.X, color.Y, color.Z), toMeters(thickness));
        return lineId;
    }
    createUiDashedLine(start, end, color, thickness) {
        const lineId = this.ipexViewer.create_ui_line(LineType.DASHED, toIVector3(start), toIVector3(end), rgb(color.X, color.Y, color.Z), toMeters(thickness));
        return lineId;
    }
    updateUiLine(id, start, end, color, thickness) {
        this.ipexViewer.update_ui_line(id, toIVector3(start), toIVector3(end), rgb(color.X, color.Y, color.Z), toMeters(thickness));
    }
    removeUiLine(id) {
        this.ipexViewer.delete_ui_line(id);
    }
    uiLineExists(id) {
        return this.ipexViewer.ui_line_exists(id);
    }
    createGhostModel(modelUrl, modelLoaded, r = 0, g = 0, b = 1, alpha = 0.5) {
        const id = this.ipexViewer.load_model(modelUrl);
        this.ipexViewer
            .object_ready(id)
            .then(() => {
            const newMaterial = this.ipexViewer.create_material();
            this.ipexViewer.set_opacity(newMaterial, alpha);
            this.ipexViewer.set_base_color(newMaterial, rgb(r, g, b));
            this.ipexViewer.set_roughness(newMaterial, 0);
            this.ipexViewer.set_metallic(newMaterial, 0);
            this.ipexViewer.set_material(id, newMaterial);
            this.ipexViewer.set_outline_block(id, false, true);
            this.ipexViewer.set_cast_shadow(id, false, true);
            this.ipexViewer.set_receive_shadow(id, false, true);
            this.ipexViewer.set_visible(id, true, true);
            const viewerObject = new IPEXViewerObject(id, this.ipexViewer, this.entityManager);
            modelLoaded(viewerObject);
        })
            .catch(() => {
            console.log(`Failed to load ghost ${modelUrl}`);
        });
    }
}
//# sourceMappingURL=IPEXViewerCoreAdapter.js.map